import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { GlobeX } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmailLink } from '~/components/common/email-link';
import { Button } from '~/components/ui/button';
import { fetchUserAfterOAuth } from '~/features/auth/services/auth.service';
import {
  consumePostLoginRedirect,
  resolvePostLoginRedirect,
} from '~/features/auth/utils/post-login-redirect';
import { consumeBridgeReturn } from '~/features/auth/utils/sso-bridge';
import { wakeServices } from '~/features/integrations/services/health.service';
import { useAuthStore } from '~/store/auth.store';
import { extractApiError } from '~/utils/extract-api-error';

const SUPPORT_EMAIL = 'support@lily.ai';

/**
 * The most recently bridged auth code. Module-level so React StrictMode's
 * double-fired effect cannot exchange the code on its second pass after
 * the first pass consumed the bridge stash and navigated away — single-use
 * codes would otherwise be invalidated before localhost can claim them.
 */
let lastBridgedCode: string | null = null;

/**
 * OAuth callback handler page.
 * Receives the auth code from the IdP redirect, exchanges it for tokens,
 * hydrates the auth store, and redirects to the home page.
 *
 * Shared by /auth/callback and /auth/$provider/callback routes.
 */
export function AuthCallbackPage() {
  const { t } = useTranslation('auth');
  const { code }: { code?: string } = useSearch({ strict: false });
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  // `t` is read through a ref so it is NOT an effect dependency. With i18next's
  // `useSuspense: false`, `t` gets a fresh reference the moment the `auth`
  // namespace finishes loading over HTTP. When that landed mid-exchange it
  // re-ran the effect below, which aborted the in-flight /me and re-POSTed the
  // single-use OAuth code — a second exchange that 401s and fails the login
  // intermittently. See docs/changes/2026-08-12-oauth-double-code-exchange-login-failure-rca.md.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  // Holds the surfaced error message (backend copy when available, generic fallback otherwise).
  // Null = no error yet; non-null = render the error screen.
  const [error, setError] = useState(code ? null : t('callback.errorDescription'));

  useEffect(() => {
    if (!code) return;

    // SSO-bridge return: this callback is happening on the cloud FE acting
    // as bridge for a localhost dev session. Forward the auth code without
    // exchanging it — the localhost FE will exchange it via the same
    // backend. Guarded against StrictMode double-fire so the second pass
    // can't fall through and consume the single-use code itself.
    if (lastBridgedCode === code) return;
    const bridgeReturn = consumeBridgeReturn();
    if (bridgeReturn) {
      lastBridgedCode = code;
      window.location.href = `${bridgeReturn}/auth/callback?code=${encodeURIComponent(code)}`;
      return;
    }

    const controller = new AbortController();

    async function handleCallback() {
      try {
        const user = await fetchUserAfterOAuth(code ?? '', controller.signal);
        setUser(user);
        // Warm every backend service (min-instances=0 → 1) right after a
        // successful SSO login. Fire-and-forget — never blocks the redirect.
        wakeServices();
        // Consume the stash AFTER the await so React StrictMode's double-fired
        // effect can't clear it on the first (aborted) pass and leave the
        // second pass without a target. The first pass throws via the aborted
        // /me request, lands in the catch, and never reaches this line.
        const stashedTarget = consumePostLoginRedirect();
        const dest = await resolvePostLoginRedirect(stashedTarget);
        void navigate({ to: dest });
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(extractApiError(err, tRef.current('callback.errorDescription')));
      }
    }

    void handleCallback();

    return () => {
      controller.abort();
    };
    // `t` intentionally excluded — see the tRef note above. Only a new `code`
    // (or unmount) should re-run this; `navigate`/`setUser` are stable.
  }, [code, navigate, setUser]);

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex max-w-md flex-col items-center text-center">
          <GlobeX
            aria-hidden="true"
            className="text-muted-foreground mb-4 h-14 w-14"
            strokeWidth={1.5}
          />
          <h2 className="text-xl font-semibold">{t('callback.error')}</h2>
          <p className="text-muted-foreground mt-2">{error}</p>
          <p className="text-muted-foreground mt-2 text-sm">
            {t('callback.contactSupport')} <EmailLink email={SUPPORT_EMAIL} />
          </p>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/login">{t('callback.backToLogin')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <p className="text-muted-foreground">{t('callback.loading')}</p>
    </div>
  );
}
